/**
 * P34 (Pedro 2026-10-13/14) — the Email button on the Agreement and Person pages.
 * Backend: routers/email_drafts.py. The result is an unsent email file (.eml) that opens in
 * Outlook as a new message; the app sends nothing.
 *
 * Who: Admin, Compliance and RM (`canEmailDrafts`). To: people of the record only, by the
 * email on their record — a person without one is refused, and the record is updated first.
 */
import { IaaExportBlockedError, type IaaExportMissingRecord } from './iaaExports';
import type { DocumentsZipRequestBody } from './documentsZip';
import type { DocumentLanguage, UUID } from './types';

export type EmailRecordType = 'agreement' | 'person';

/** Answer 7: Admin, Compliance and RMs only (not Staff, not Read-only). */
export const canEmailDrafts = (role: string | null | undefined): boolean =>
  role === 'admin' || role === 'compliance' || role === 'rm';

export interface EmailRecipientOption {
  person_id: UUID;
  name: string;
  /** "Holder", "Representative · Beneficiary", "KYC contact", "This person". */
  roles: string;
  /** Null = no email on file: shown, cannot be picked. */
  email: string | null;
  /** M / F / O / null: the Portuguese greeting (Caro / Cara / Caro(a)). */
  gender: string | null;
}

export interface EmailDocumentOption {
  id: UUID;
  record_label: string;
  kind_label: string;
  title: string | null;
  filename: string;
  size_bytes: number;
}

export interface EmailLibraryOption { id: UUID; title: string; filename: string; size_bytes: number }

export interface EmailOptions {
  record: { type: EmailRecordType; id: UUID; label: string };
  language: DocumentLanguage;
  recipients: EmailRecipientOption[];
  /** The app's other users, offered for Cc (EM-CC; none ticked by default). */
  colleagues: { user_id: UUID; name: string; email: string }[];
  /** Agreement only (null on a person): months with a recorded monthly report. */
  monthly_reports: { period_month: string; filename: string }[] | null;
  documents: EmailDocumentOption[];
  library: EmailLibraryOption[];
  limit_bytes: number;
}

export type EmailTemplateKind =
  | 'clientform_individual' | 'clientform_legal' | 'iaa' | 'fees' | 'aq' | 'pp' | 'samplemonthly';

export interface EmailTemplatePick { kind: EmailTemplateKind; language: DocumentLanguage; format: 'word' | 'pdf' }

export interface EmailDraftSpec {
  recipients: UUID[];
  cc_users?: UUID[];
  subject: string;
  body: string;
  generated?: DocumentsZipRequestBody;
  monthly_reports?: string[];
  documents?: UUID[];
  library?: UUID[];
  templates?: EmailTemplatePick[];
}

export interface EmailDraftResult { blob: Blob; fileName: string; skipped: number; skippedDetail: string | null }

const base = (type: EmailRecordType, id: UUID) =>
  `/email-drafts/${type === 'agreement' ? 'agreements' : 'persons'}/${id}`;

async function errorOf(res: Response, fallback: string): Promise<Error> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON */ }
  if (res.status === 409 && detail && typeof detail === 'object'
      && (detail as { code?: string }).code === 'iaa_export_blocked') {
    const d = detail as { message: string; missing: IaaExportMissingRecord[] };
    return new IaaExportBlockedError(d.message, d.missing);
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return new Error(String((detail as { message: unknown }).message));
  }
  return new Error(typeof detail === 'string' ? detail : fallback);
}

export async function fetchEmailOptions(type: EmailRecordType, id: UUID): Promise<EmailOptions> {
  const res = await fetch(`${base(type, id)}/options`, { credentials: 'include' });
  if (!res.ok) throw await errorOf(res, 'The email options could not be loaded.');
  return res.json();
}

export async function fetchDefaultText(
  language: DocumentLanguage, recipients: { name: string; gender: string | null }[], documents: string[],
): Promise<{ subject: string; body: string }> {
  const res = await fetch('/email-drafts/default-text', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language, recipients, documents }),
  });
  if (!res.ok) throw await errorOf(res, 'The standard text could not be loaded.');
  return res.json();
}

export async function prepareEmailDraft(
  type: EmailRecordType, id: UUID, spec: EmailDraftSpec, others: File[],
): Promise<EmailDraftResult> {
  const form = new FormData();
  form.append('spec', JSON.stringify(spec));
  others.forEach((f) => form.append('files', f, f.name));
  const res = await fetch(base(type, id), { method: 'POST', credentials: 'include', body: form });
  if (!res.ok) throw await errorOf(res, 'The email draft could not be prepared.');
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const skippedRaw = res.headers.get('X-Email-Skipped');
  const detailRaw = res.headers.get('X-Email-Skipped-Detail');
  return {
    blob: await res.blob(),
    fileName: m ? decodeURIComponent(m[1]) : 'email_draft.eml',
    skipped: skippedRaw ? parseInt(skippedRaw, 10) : 0,
    skippedDetail: detailRaw ? decodeURIComponent(detailRaw) : null,
  };
}
